import { router } from "@inertiajs/react";

export interface PaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
  prev_page: number | null;
  next_page: number | null;
}

interface TablePaginationProps {
  pagination: PaginationMeta;
  /** Query param this table pages on, e.g. "subject_lines_page" */
  pageParam?: string;
  /**
   * Take over the navigation. The offers list needs this: its search box and
   * sort live in component state, and a plain URL rewrite would drop them.
   */
  onPageChange?: (page: number) => void;
  /**
   * Limit the response to these props. Offer tabs pass their own prop names so
   * changing page re-queries that tab alone, leaving the other seven untouched.
   */
  reloadOnly?: string[];
  /** Plural noun for the count, e.g. "subject lines" */
  label?: string;
}

export default function TablePagination({
  pagination,
  pageParam = "page",
  onPageChange,
  reloadOnly,
  label = "results",
}: TablePaginationProps) {
  if (pagination.total_pages <= 1) return null;

  const goToPage = (page: number | null) => {
    if (!page) return;
    if (onPageChange) {
      onPageChange(page);
      return;
    }
    // Keep every other param -- the active tab and the other tables' pages all
    // live in the query string, and dropping them would reset the view.
    const params = new URLSearchParams(window.location.search);
    params.set(pageParam, String(page));
    router.get(
      `${window.location.pathname}?${params.toString()}`,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        ...(reloadOnly?.length ? { only: reloadOnly } : {}),
      }
    );
  };

  // Window of at most 5 page buttons, kept centred on the current page
  const { current_page, total_pages, total_count, per_page } = pagination;
  const pageNumbers = Array.from(
    { length: Math.min(5, total_pages) },
    (_, i) => {
      if (total_pages <= 5) return i + 1;
      if (current_page <= 3) return i + 1;
      if (current_page >= total_pages - 2) return total_pages - 4 + i;
      return current_page - 2 + i;
    }
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg mt-4 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing{" "}
          <span className="font-medium">
            {(current_page - 1) * per_page + 1}
          </span>{" "}
          to{" "}
          <span className="font-medium">
            {Math.min(current_page * per_page, total_count)}
          </span>{" "}
          of <span className="font-medium">{total_count}</span> {label}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => goToPage(pagination.prev_page)}
            disabled={!pagination.prev_page}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Previous
          </button>

          <div className="flex items-center space-x-1">
            {pageNumbers.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                className={`px-3 py-2 text-sm font-medium border rounded-md cursor-pointer ${
                  pageNum === current_page
                    ? "bg-gradient-to-b from-blue-500 to-blue-600 text-white border-blue-600"
                    : "text-gray-500 bg-white border-gray-300 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            ))}
          </div>

          <button
            onClick={() => goToPage(pagination.next_page)}
            disabled={!pagination.next_page}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
